'use client';

import { useEffect, useMemo, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

export type PerformanceProfile = {
  reducedMotion: boolean;
  isMobile: boolean;
  isCoarsePointer: boolean;
  saveData: boolean;
  isLite: boolean;
  /** Large desktop + fine pointer only — enables minimal ambient loops */
  isRich: boolean;
  loop: number;
  enableParallax: boolean;
  enableInfinite: boolean;
  metricIntervalMs: number;
  titleIntervalMs: number;
};

export function usePerformanceProfile(): PerformanceProfile {
  const reducedMotionPref = useReducedMotion();
  const [flags, setFlags] = useState({
    isMobile: false,
    isCoarsePointer: false,
    saveData: false,
    isLargeDesktop: false,
  });

  useEffect(() => {
    const mobileMq = window.matchMedia('(max-width: 1023px)');
    const coarseMq = window.matchMedia('(pointer: coarse)');
    const saveMq = window.matchMedia('(prefers-reduced-data: reduce)');
    const largeMq = window.matchMedia('(min-width: 1280px) and (pointer: fine)');

    const update = () => {
      setFlags({
        isMobile: mobileMq.matches,
        isCoarsePointer: coarseMq.matches,
        saveData: saveMq.matches,
        isLargeDesktop: largeMq.matches,
      });
    };

    update();
    mobileMq.addEventListener('change', update);
    coarseMq.addEventListener('change', update);
    saveMq.addEventListener('change', update);
    largeMq.addEventListener('change', update);

    return () => {
      mobileMq.removeEventListener('change', update);
      coarseMq.removeEventListener('change', update);
      saveMq.removeEventListener('change', update);
      largeMq.removeEventListener('change', update);
    };
  }, []);

  useEffect(() => {
    const reducedMotion = Boolean(reducedMotionPref);
    const isLite =
      reducedMotion || flags.isMobile || flags.isCoarsePointer || flags.saveData;
    const isRich = !isLite && flags.isLargeDesktop;

    document.documentElement.classList.toggle('perf-lite', isLite);
    document.documentElement.classList.toggle('perf-rich', isRich);
    document.documentElement.classList.toggle('motion-calm', !isRich);

    return () => {
      document.documentElement.classList.remove('perf-lite', 'perf-rich', 'motion-calm');
    };
  }, [reducedMotionPref, flags]);

  return useMemo(() => {
    const reducedMotion = Boolean(reducedMotionPref);
    const { isMobile, isCoarsePointer, saveData, isLargeDesktop } = flags;
    const isLite = reducedMotion || isMobile || isCoarsePointer || saveData;
    const isRich = !isLite && isLargeDesktop;

    return {
      reducedMotion,
      isMobile,
      isCoarsePointer,
      saveData,
      isLite,
      isRich,
      loop: isRich ? Infinity : 0,
      enableParallax: false,
      enableInfinite: isRich,
      metricIntervalMs: isLite ? 6500 : 4200,
      titleIntervalMs: isLite ? 4200 : 3200,
    };
  }, [reducedMotionPref, flags]);
}
